import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnInit,
  inject,
  DestroyRef,
} from '@angular/core';
import { CommentEntityNameEnum } from '../../models';
import { CommentPanelManagerService, CommentingUserEventsService, FocusingService } from '../../services';
import { createComposedResourceId } from '../../utils';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-abstract-comment-button',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbstractCommentButtonComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @HostBinding('class.hidden')
  private isNotReady = true;

  private currentCd: ChangeDetectorRef;
  private focusingService: FocusingService;
  private parentCd: ChangeDetectorRef;

  protected commentPanelManagerService: CommentPanelManagerService;
  protected commentingUserEventsService: CommentingUserEventsService;
  protected elementRef: ElementRef<HTMLElement>;

  @Input()
  resourceId: string;

  @Input()
  resourceType: string;

  @HostBinding('class.disabled')
  @Input()
  disabled: boolean;

  @HostBinding('class.focused')
  isFocused: boolean;

  commentsAndRepliesCount: number;

  @HostBinding('class.threads-exist')
  get commentsAndRepliesExist(): boolean {
    return !!this.commentsAndRepliesCount;
  }

  constructor(protected injector: Injector) {
    this.commentPanelManagerService = injector.get(CommentPanelManagerService);
    this.commentingUserEventsService = injector.get(CommentingUserEventsService);
    this.elementRef = injector.get(ElementRef<HTMLElement>);
    this.currentCd = injector.get(ChangeDetectorRef);
    this.focusingService = injector.get(FocusingService);
    // See more here: https://github.com/angular/angular/issues/22560
    this.parentCd = injector.get(ChangeDetectorRef, undefined, { skipSelf: true });
  }

  ngOnInit(): void {
    // see SCSS file for the explanation
    this.elementRef.nativeElement.parentElement.classList.add('panel-trigger-parent');
    const threadsAndRepliesCountForResource$ = this.commentPanelManagerService.getThreadsAndRepliesCountForResource(
      this.resourceType,
      this.resourceId
    );

    threadsAndRepliesCountForResource$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => {
        this.commentsAndRepliesCount = count;
        delete this.isNotReady;
        this.detectChanges();
      });

    this.commentPanelManagerService
      .isResourceFocused(this.resourceType, this.resourceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isFocused: boolean) => {
        this.isFocused = isFocused;
        this.detectChanges();
      });
  }

  @HostListener('click', ['$event'])
  async onClick(event?: MouseEvent): Promise<void> {
    event?.stopPropagation();

    if (this.disabled) {
      return;
    }

    const waitingForThreadsLoading$ = this.commentPanelManagerService.areThreadsLoaded().pipe(filter(Boolean));
    await firstValueFrom(waitingForThreadsLoading$);

    const displayedSingleThreadResourceId = await firstValueFrom(this.commentPanelManagerService.getDisplayedSingleThreadResourceId());
    const isTheSameResourceDisplayed = displayedSingleThreadResourceId === this.resourceId;

    if (isTheSameResourceDisplayed) { return; }

    if (this.commentsAndRepliesExist) {
      this.commentPanelManagerService.focusComment(this.resourceType, this.resourceId);
    } else {
      this.commentPanelManagerService.createThread(this.resourceType, this.resourceId);
    }

    this.commentPanelManagerService.displaySingleThread(this.resourceType, this.resourceId);

    this.focusingService.focusSingleResource(
      CommentEntityNameEnum.Resource,
      createComposedResourceId(this.resourceType, this.resourceId)
    );
  }

  private detectChanges(): void {
    // For some reason, when we call the components change detection
    // it doesn't take into account hostbindings and some classes get lost from the host element
    // but if we use call parentCd.detectChanges it doesn't detect changes in the component and the template doesn't render as we need
    // as a workaround we end up with calling both ChangeDetectorRef's
    this.currentCd.detectChanges(); // Updates the component's template
    this.parentCd.detectChanges(); // Updates host bindings. See more here: https://github.com/angular/angular/issues/22560
  }
}
